/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { generateVizLinkForm, generateVizLinkWebhook } from './asyncThunks'
import { VisualizationSlice, WidgetVisualizationState } from './types'

const initialState: VisualizationSlice = {}

const createDefaultWidgetFormState = () => ({
  status: 'idle' as const,
  error: null
})

const createDefaultWidgetWebhookState = () => ({
  status: 'idle' as const,
  error: null,
  response: null
})

const createDefaultWidgetState = (): WidgetVisualizationState => ({
  form: createDefaultWidgetFormState(),
  webhook: createDefaultWidgetWebhookState()
})

const visualizationSlice = createSlice({
  name: 'visualization',
  initialState,
  reducers: {
    clearWebhookResponse: (state, action: PayloadAction<string>) => {
      const widgetId = action.payload
      if (state[widgetId]?.webhook) {
        state[widgetId].webhook!.response = null
        state[widgetId].webhook!.error = null
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateVizLinkForm.pending, (state, action) => {
        const widgetId = action.meta.arg.widgetSetting.widgetId!
        if (!state[widgetId]) {
          state[widgetId] = createDefaultWidgetState()
        }

        const widgetState = state[widgetId]
        if (!widgetState.form) {
          widgetState.form = createDefaultWidgetFormState()
        }
        widgetState.form.status = 'loading'
        widgetState.form.error = null
      })
      .addCase(generateVizLinkForm.fulfilled, (state, action) => {
        const widgetId = action.meta.arg.widgetSetting.widgetId!
        if (!state[widgetId]) {
          state[widgetId] = createDefaultWidgetState()
        }

        const widgetState = state[widgetId]
        if (!widgetState.form) {
          widgetState.form = createDefaultWidgetFormState()
        }
        widgetState.form.status = 'succeeded'
        widgetState.form.error = null
      })
      .addCase(generateVizLinkForm.rejected, (state, action) => {
        const widgetId = action.meta.arg.widgetSetting.widgetId!
        if (!state[widgetId]) {
          state[widgetId] = createDefaultWidgetState()
        }

        const widgetState = state[widgetId]
        if (!widgetState.form) {
          widgetState.form = createDefaultWidgetFormState()
        }
        widgetState.form.status = 'failed'
        widgetState.form.error =
          action.payload?.error ||
          action.error.message ||
          'Failed to submit form for visualization link'
      })
      .addCase(generateVizLinkWebhook.pending, (state, action) => {
        const widgetId = action.meta.arg.widgetSetting.widgetId!
        if (!state[widgetId]) {
          state[widgetId] = createDefaultWidgetState()
        }

        const widgetState = state[widgetId]
        if (!widgetState.webhook) {
          widgetState.webhook = createDefaultWidgetWebhookState()
        }
        widgetState.webhook.status = 'loading'
        widgetState.webhook.error = null
      })
      .addCase(generateVizLinkWebhook.fulfilled, (state, action) => {
        const widgetId = action.meta.arg.widgetSetting.widgetId!
        if (!state[widgetId]) {
          state[widgetId] = createDefaultWidgetState()
        }

        const widgetState = state[widgetId]
        if (!widgetState.webhook) {
          widgetState.webhook = createDefaultWidgetWebhookState()
        }
        widgetState.webhook.status = 'succeeded'
        widgetState.webhook.error = null
        widgetState.webhook.response = action.payload
      })
      .addCase(generateVizLinkWebhook.rejected, (state, action) => {
        const widgetId = action.meta.arg.widgetSetting.widgetId!
        if (!state[widgetId]) {
          state[widgetId] = createDefaultWidgetState()
        }

        const widgetState = state[widgetId]
        if (!widgetState.webhook) {
          widgetState.webhook = createDefaultWidgetWebhookState()
        }
        widgetState.webhook.status = 'failed'
        widgetState.webhook.error =
          action.payload?.error || action.error.message || 'Failed to call webhook'
        widgetState.webhook.response = null
      })
  }
})

export const { clearWebhookResponse } = visualizationSlice.actions
export default visualizationSlice.reducer
